// What the merchant changed, shown before the customer approves and pays.
//
// The handoff requires the quote to show "every change the merchant made to
// the cart (quantity, price, removed items) before the customer pays". Asking
// someone to approve a total without telling them a $3 item became $5, or that
// two things are missing, is exactly where trust is lost — and it is the most
// common reason a dispute gets opened later.
//
// Renders nothing when the quote matches the cart, so an honest quote adds no
// noise.

import { ArrowRight, MinusCircle, PlusCircle, TrendingUp } from 'lucide-react';
import { diffQuote, type QuoteLineChange } from '../../lib/quoteDiff';
import type { QuoteItem, RequestItem } from '../../lib/api';

const htg = (minor: number) => `HTG ${(minor / 100).toLocaleString()}`;

/** Trim trailing zeros so "2.00" reads as "2". */
const qty = (n: number) => (Number.isInteger(n) ? String(n) : String(n));

function Line({ c }: { c: QuoteLineChange }) {
  if (c.kind === 'removed') {
    return (
      <li className="flex items-start gap-2 py-1.5">
        <MinusCircle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[var(--color-danger-fg)]" aria-hidden="true" />
        <span className="text-[12.5px] text-[var(--color-body)]">
          <span className="font-semibold">{c.name}</span>
          {' — '}
          {/* Named explicitly: "not available" is a fact the customer may want
              to act on, not a silent omission. */}
          <span className="text-[var(--color-danger-fg)] font-semibold">not available</span>
          <span className="text-[var(--color-muted)]"> (was {qty(c.before!.quantity)} × {htg(c.before!.unitHtgMinor)})</span>
        </span>
      </li>
    );
  }

  if (c.kind === 'added') {
    return (
      <li className="flex items-start gap-2 py-1.5">
        <PlusCircle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[var(--color-success-fg)]" aria-hidden="true" />
        <span className="text-[12.5px] text-[var(--color-body)]">
          <span className="font-semibold">{c.name}</span>
          {' — '}
          <span className="text-[var(--color-success-fg)] font-semibold">added</span>
          <span className="text-[var(--color-muted)]"> ({qty(c.after!.quantity)} × {htg(c.after!.unitHtgMinor)})</span>
        </span>
      </li>
    );
  }

  const priceMoved = c.before!.unitHtgMinor !== c.after!.unitHtgMinor;
  const qtyMoved = c.before!.quantity !== c.after!.quantity;

  return (
    <li className="flex items-start gap-2 py-1.5">
      <TrendingUp className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[var(--color-warning-fg)]" aria-hidden="true" />
      <span className="text-[12.5px] text-[var(--color-body)]">
        <span className="font-semibold">{c.name}</span>
        {qtyMoved && (
          <span className="text-[var(--color-secondary-2)]">
            {' — qty '}
            <span className="tabular-nums">{qty(c.before!.quantity)}</span>
            <ArrowRight className="inline w-3 h-3 mx-0.5" aria-label="changed to" />
            <span className="tabular-nums font-semibold text-[var(--color-ink)]">{qty(c.after!.quantity)}</span>
          </span>
        )}
        {priceMoved && (
          <span className="text-[var(--color-secondary-2)]">
            {qtyMoved ? ', ' : ' — '}
            {'unit '}
            <span className="tabular-nums">{htg(c.before!.unitHtgMinor)}</span>
            <ArrowRight className="inline w-3 h-3 mx-0.5" aria-label="changed to" />
            <span className="tabular-nums font-semibold text-[var(--color-ink)]">{htg(c.after!.unitHtgMinor)}</span>
          </span>
        )}
      </span>
    </li>
  );
}

export function QuoteChanges({
  requestItems,
  quoteItems,
}: {
  requestItems: RequestItem[];
  quoteItems: QuoteItem[];
}) {
  const diff = diffQuote(requestItems ?? [], quoteItems ?? []);

  // An unchanged quote needs no explanation.
  if (!diff.hasChanges) return null;

  const parts = [
    diff.removedCount && `${diff.removedCount} unavailable`,
    diff.repricedCount && `${diff.repricedCount} repriced`,
    diff.addedCount && `${diff.addedCount} added`,
  ].filter(Boolean);

  return (
    <div className="mt-3 rounded-lg border border-[var(--color-warning-border)] bg-[var(--color-warning-bg)]/60 p-3">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-[var(--color-warning-fg)]">
        What the merchant changed
      </p>
      <p className="mt-0.5 text-[11.5px] text-[var(--color-warning-fg)]/80">
        {parts.join(' · ')} — check before you approve.
      </p>
      <ul className="mt-1.5 divide-y divide-[var(--color-warning-border)]/60">
        {diff.changed.map((c, i) => <Line key={`${c.kind}-${c.name}-${i}`} c={c} />)}
      </ul>
    </div>
  );
}
